/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { printStyles } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* eslint-disable */
const HTMLEllipsis = require("@eisinc/Libraries/react-lines-ellipsis/html.js");
/* eslint-enable */

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasProduct;

export interface ProductDetailsDescriptionStyles {
    wrapper?: InjectableCss;
    brandWrapper?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    brandStyles?: ProductBrandStyles;
}

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    wrapper: {
        css: css`
            margin: 10px 0;
        `,
    },
    brandWrapper: {
        css: css`
            margin: 10px 0;
        `,
    },
    descriptionWrapper: {
        css: css`
            font-size: 14px;
            letter-spacing: 0;
            line-height: 18px;

            .read-more-link,
            a {
                cursor: pointer;
                color: ${getColor("primary")};

                &:hover {
                    text-decoration: underline;
                }
            }

            ${printStyles.avoidBreakInside}
        `,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    width: auto;
                    height: auto;
                    max-width: 100px;
                    max-height: 100px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
};

const styles = descriptionStyles;

const ProductDetailsDescription: React.FC<Props> = ({ product, settingsCollection }) => {
    const [useEllipsis, setUseEllipsis] = React.useState(true);
    const maxLength =
        (settingsCollection.customSettings as any).maximumLengthToShowReadMore &&
        Number((settingsCollection.customSettings as any).maximumLengthToShowReadMore);

    if (!product.content) {
        return null;
    }

    const handleTextClick = (e: any) => {
        const target = e.target;

        if (target.id === "ProductDetailsDescription_readMore") {
            e.preventDefault();
            setUseEllipsis(false);
        }
    };

    const ReadLessNode = ` <span class="read-more-link">${translate("read less")}</span>`;
    const elilipsisNode = `... <span id="ProductDetailsDescription_readMore" class="read-more-link">${translate(
        "read more",
    )}</span>`;

    return (
        <StyledWrapper {...styles.wrapper}>
            {product.brand && (
                <StyledWrapper {...styles.brandWrapper}>
                    <ProductBrand brand={product.brand} showLogo={true} extendedStyles={styles.brandStyles} />
                </StyledWrapper>
            )}
            {product.content.htmlContent && (
                <>
                    {useEllipsis && maxLength && (
                        <StyledWrapper {...styles.descriptionWrapper} onClick={handleTextClick}>
                            <HTMLEllipsis
                                data-test-selector="productDetails_htmlContent"
                                unsafeHTML={product.content.htmlContent}
                                maxLine={maxLength}
                                ellipsisHTML={elilipsisNode}
                                basedOn="letters"
                            />
                        </StyledWrapper>
                    )}
                    {(!useEllipsis || !maxLength) && (
                        <StyledWrapper {...styles.descriptionWrapper} onClick={() => setUseEllipsis(true)}>
                            {/* eslint-disable react/no-danger  */}
                            <div
                                data-test-selector="productDetails_htmlContent"
                                dangerouslySetInnerHTML={{ __html: product.content.htmlContent + ReadLessNode }}
                            />
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsDescription)),
    definition: {
        displayName: "Description",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
